import { Rectangle } from '../geom/Rectangle';
import { Point } from '../geom/Point';
import type { Matrix } from '../geom/Matrix';
import type { ColorTransform } from '../geom/ColorTransform';
import { BitmapFilter, BlurFilter, ColorMatrixFilter } from '../filters/filters';
import { roundToEven, toFixed8 } from '../utils/numeric';
import { FLASH_PREMUL_FACTOR } from './premul';
import { drawSourceInto } from './raster';
import { rt } from '../player/runtime';

// ---------------------------------------------------------------- pixel helpers (premultiplied ARGB)

/** Straight ARGB -> premultiplied ARGB (Flash rounding: (c*a+127)/255). Opaque bitmaps force alpha 255. */
export function premultiply(argb: number, transparent: boolean): number {
  const a = transparent ? argb >>> 24 : 255;
  if (a === 255) return (argb | 0xff000000) >>> 0;
  if (a === 0) return 0;
  const r = ((((argb >>> 16) & 255) * a + 127) / 255) | 0;
  const g = ((((argb >>> 8) & 255) * a + 127) / 255) | 0;
  const b = (((argb & 255) * a + 127) / 255) | 0;
  return ((a << 24) | (r << 16) | (g << 8) | b) >>> 0;
}

/** Premultiplied ARGB -> straight ARGB using Flash's brute-forced factor table (lossy). */
export function unmultiply(p: number): number {
  const a = p >>> 24;
  if (a === 255) return p >>> 0;
  if (a === 0) return 0;
  const f = FLASH_PREMUL_FACTOR[a];
  const r = Math.floor((((p >>> 16) & 255) * f + 0x8000) / 65536) & 255;
  const g = Math.floor((((p >>> 8) & 255) * f + 0x8000) / 65536) & 255;
  const b = Math.floor(((p & 255) * f + 0x8000) / 65536) & 255;
  return ((a << 24) | (r << 16) | (g << 8) | b) >>> 0;
}

/** Source-over blend of premultiplied colours (formula verified against Flash by Ruffle). */
export function blendOver(dest: number, src: number): number {
  const sa = src >>> 24;
  if (sa === 255) return src >>> 0;
  const inv = 256 - sa;
  const r = (((src >>> 16) & 255) + ((((dest >>> 16) & 255) * inv) >> 8)) & 255;
  const g = (((src >>> 8) & 255) + ((((dest >>> 8) & 255) * inv) >> 8)) & 255;
  const b = ((src & 255) + (((dest & 255) * inv) >> 8)) & 255;
  const a = (sa + (((dest >>> 24) * inv) >> 8)) & 255;
  return ((a << 24) | (r << 16) | (g << 8) | b) >>> 0;
}

function invalidBitmapData(): Error {
  const e = new Error('ArgumentError: Error #2015: Invalid BitmapData.');
  e.name = 'ArgumentError';
  return e;
}

/** Rectangle -> integer (x, y, w, h), computing x+width in floating point then rounding half-to-even. */
export function rectToInts(r: Rectangle): [number, number, number, number] {
  const x = +r.x;
  const y = +r.y;
  const xMax = roundToEven(x + +r.width);
  const yMax = roundToEven(y + +r.height);
  const xi = roundToEven(x);
  const yi = roundToEven(y);
  return [xi, yi, (xMax - xi) | 0, (yMax - yi) | 0];
}

/** PixelRegion::for_region_i32 (normalizes negative sizes), clamped to [0, w] x [0, h]. */
function regionClamped(x: number, y: number, w: number, h: number, W: number, H: number): [number, number, number, number] {
  const ax = x;
  const ay = y;
  const bx = x + w;
  const by = y + h;
  let x0 = Math.max(Math.min(ax, bx), 0);
  let y0 = Math.max(Math.min(ay, by), 0);
  let x1 = Math.max(Math.max(ax, bx), 0);
  let y1 = Math.max(Math.max(ay, by), 0);
  x0 = Math.min(x0, W);
  y0 = Math.min(y0, H);
  x1 = Math.min(x1, W);
  y1 = Math.min(y1, H);
  return [x0, y0, x1, y1];
}

/**
 * PixelRegion::clamp_with_intersection: returns [destX0, destY0, srcX0, srcY0, w, h] or null.
 */
function clampWithIntersection(
  destW: number,
  destH: number,
  dx: number,
  dy: number,
  srcW: number,
  srcH: number,
  sx: number,
  sy: number,
  w: number,
  h: number,
): [number, number, number, number, number, number] | null {
  // Both regions translated so that the copy origin is (0, 0).
  const x0 = Math.max(-dx, -sx, 0);
  const y0 = Math.max(-dy, -sy, 0);
  const x1 = Math.min(destW - dx, srcW - sx, w);
  const y1 = Math.min(destH - dy, srcH - sy, h);
  if (x1 <= x0 || y1 <= y0) return null;
  return [x0 + dx, y0 + dy, x0 + sx, y0 + sy, x1 - x0, y1 - y0];
}

type ThresholdOp = (a: number, b: number) => boolean;
const THRESHOLD_OPS: Record<string, ThresholdOp> = {
  '<': (a, b) => a < b,
  '<=': (a, b) => a <= b,
  '>': (a, b) => a > b,
  '>=': (a, b) => a >= b,
  '==': (a, b) => a === b,
  '!=': (a, b) => a !== b,
};

/** Adds a changed rectangle to a list of at most 4: merged into one it touches, or (list full) the cheapest one. */
function addDirtyRect(list: Array<[number, number, number, number]>, x0: number, y0: number, x1: number, y1: number): void {
  let best = -1;
  let bestGrowth = Infinity;
  for (let i = 0; i < list.length; i++) {
    const r = list[i];
    const touches = x0 <= r[2] && x1 >= r[0] && y0 <= r[3] && y1 >= r[1];
    const growth = (Math.max(x1, r[2]) - Math.min(x0, r[0])) * (Math.max(y1, r[3]) - Math.min(y0, r[1])) - (r[2] - r[0]) * (r[3] - r[1]);
    if (touches) {
      best = i;
      break;
    }
    if (growth < bestGrowth) (best = i), (bestGrowth = growth);
  }
  if (best < 0 || (list.length < 4 && !(x0 <= list[best][2] && x1 >= list[best][0] && y0 <= list[best][3] && y1 >= list[best][1]))) {
    list.push([x0, y0, x1, y1]);
    return;
  }
  const r = list[best];
  if (x0 < r[0]) r[0] = x0;
  if (y0 < r[1]) r[1] = y0;
  if (x1 > r[2]) r[2] = x1;
  if (y1 > r[3]) r[3] = y1;
}

/**
 * flash.display.BitmapData — pure TypeScript, 32-bit premultiplied ARGB storage (`$data`, one
 * Uint32 per pixel as 0xAARRGGBB), reproducing Flash's premultiplication precision loss.
 *
 * Renderer seam: `$data`, `$version` (incremented on every change) and `$dirty` (accumulated
 * changed region since the last `$takeDirty()`/`$takeDirtyRects()`), plus `toRGBA()` for upload.
 */
export class BitmapData {
  [key: string]: any;

  /** @internal */ $width: number;
  /** @internal */ $height: number;
  /** @internal */ $transparent: boolean;
  /** @internal premultiplied ARGB pixels */ $data: Uint32Array;
  /** @internal */ $disposed: boolean;
  /** @internal */ $locked: boolean;
  /** @internal */ $version: number;
  /** @internal dirty region [x0, y0, x1, y1) or null */ $dirty: [number, number, number, number] | null;
  /** @internal the same region as up to 4 rectangles: distant changes (two craters) are not merged into one */
  $dirtyRects: Array<[number, number, number, number]>;

  constructor(width: number, height: number, transparent: boolean = true, fillColor: number = 0xffffffff) {
    width = width | 0;
    height = height | 0;
    if (width <= 0 || height <= 0 || width > 8191 || height > 8191 || width * height > 16777215) throw invalidBitmapData();
    this.$width = width;
    this.$height = height;
    this.$transparent = !!transparent;
    this.$data = new Uint32Array(width * height);
    const c = premultiply(fillColor >>> 0, this.$transparent);
    if (c !== 0) this.$data.fill(c);
    this.$disposed = false;
    this.$locked = false;
    this.$version = 0;
    this.$dirty = [0, 0, width, height];
    this.$dirtyRects = [[0, 0, width, height]];
  }

  /** @internal Create without validation (library bitmaps up to any size). */
  static $fromPremultiplied(width: number, height: number, transparent: boolean, data: Uint32Array): BitmapData {
    const bd = Object.create(BitmapData.prototype) as BitmapData;
    bd.$width = width;
    bd.$height = height;
    bd.$transparent = transparent;
    bd.$data = data;
    bd.$disposed = false;
    bd.$locked = false;
    bd.$version = 0;
    bd.$dirty = [0, 0, width, height];
    bd.$dirtyRects = [[0, 0, width, height]];
    return bd;
  }

  /** @internal Build from straight RGBA8 bytes (decoded PNG). */
  static $fromRGBA(width: number, height: number, rgba: Uint8Array | Uint8ClampedArray, transparent = true): BitmapData {
    const n = width * height;
    const data = new Uint32Array(n);
    for (let i = 0, j = 0; i < n; i++, j += 4) {
      const argb = ((rgba[j + 3] << 24) | (rgba[j] << 16) | (rgba[j + 1] << 8) | rgba[j + 2]) >>> 0;
      data[i] = premultiply(argb, transparent);
    }
    return BitmapData.$fromPremultiplied(width, height, transparent, data);
  }

  /** @internal */ $check(): void {
    if (this.$disposed) throw invalidBitmapData();
  }

  /** @internal Mark a region as changed. */
  $touch(x0: number, y0: number, x1: number, y1: number): void {
    this.$version++;
    const r = rt();
    r.renderVersion++;
    if (r.changedBitmaps !== null && !this.$dchanged) {
      this.$dchanged = true;
      r.changedBitmaps.push(this);
    }
    const d = this.$dirty;
    if (!d) this.$dirty = [x0, y0, x1, y1];
    else {
      if (x0 < d[0]) d[0] = x0;
      if (y0 < d[1]) d[1] = y0;
      if (x1 > d[2]) d[2] = x1;
      if (y1 > d[3]) d[3] = y1;
    }
    addDirtyRect(this.$dirtyRects, x0, y0, x1, y1);
  }

  /** Renderer seam: take and clear the dirty region ([x0, y0, x1, y1) or null). */
  $takeDirty(): [number, number, number, number] | null {
    const d = this.$dirty;
    this.$dirty = null;
    this.$dirtyRects = [];
    return d;
  }

  /** Renderer seam: take and clear the dirty region as up to 4 rectangles covering it (null when unchanged). */
  $takeDirtyRects(): Array<[number, number, number, number]> | null {
    const d = this.$dirty;
    if (!d) return null;
    const r = this.$dirtyRects;
    this.$dirty = null;
    this.$dirtyRects = [];
    return r.length ? r : [d];
  }

  /** Renderer seam: straight RGBA8 of a region (default: whole bitmap). */
  toRGBA(out?: Uint8ClampedArray, x0 = 0, y0 = 0, x1 = this.$width, y1 = this.$height): Uint8ClampedArray {
    const w = x1 - x0;
    const h = y1 - y0;
    const buf = out ?? new Uint8ClampedArray(w * h * 4);
    const W = this.$width;
    const data = this.$data;
    let j = 0;
    for (let y = y0; y < y1; y++) {
      let i = y * W + x0;
      for (let x = x0; x < x1; x++, i++) {
        const p = unmultiply(data[i]);
        buf[j++] = (p >>> 16) & 255;
        buf[j++] = (p >>> 8) & 255;
        buf[j++] = p & 255;
        buf[j++] = p >>> 24;
      }
    }
    return buf;
  }

  get width(): number {
    this.$check();
    return this.$width;
  }
  get height(): number {
    this.$check();
    return this.$height;
  }
  get transparent(): boolean {
    this.$check();
    return this.$transparent;
  }
  get rect(): Rectangle {
    this.$check();
    return new Rectangle(0, 0, this.$width, this.$height);
  }

  // ---------------------------------------------------------------- single pixels
  getPixel(x: number, y: number): number {
    if (this.$disposed) throw invalidBitmapData();
    const xi = (x | 0) >>> 0;
    const yi = (y | 0) >>> 0;
    if (xi >= this.$width || yi >= this.$height) return 0;
    return unmultiply(this.$data[yi * this.$width + xi]) & 0xffffff;
  }

  getPixel32(x: number, y: number): number {
    if (this.$disposed) throw invalidBitmapData();
    const xi = (x | 0) >>> 0;
    const yi = (y | 0) >>> 0;
    if (xi >= this.$width || yi >= this.$height) return 0;
    const p = this.$data[yi * this.$width + xi];
    return this.$transparent ? unmultiply(p) : p >>> 0;
  }

  setPixel(x: number, y: number, color: number): void {
    if (this.$disposed) throw invalidBitmapData();
    const xi = (x | 0) >>> 0;
    const yi = (y | 0) >>> 0;
    if (xi >= this.$width || yi >= this.$height) return;
    const i = yi * this.$width + xi;
    const rgb = (color >>> 0) & 0xffffff;
    if (this.$transparent) {
      const a = this.$data[i] >>> 24;
      this.$data[i] = premultiply(((a << 24) | rgb) >>> 0, true);
    } else this.$data[i] = (0xff000000 | rgb) >>> 0;
    this.$touch(xi, yi, xi + 1, yi + 1);
  }

  setPixel32(x: number, y: number, color: number): void {
    if (this.$disposed) throw invalidBitmapData();
    const xi = (x | 0) >>> 0;
    const yi = (y | 0) >>> 0;
    if (xi >= this.$width || yi >= this.$height) return;
    this.$data[yi * this.$width + xi] = premultiply(color >>> 0, this.$transparent);
    this.$touch(xi, yi, xi + 1, yi + 1);
  }

  // ---------------------------------------------------------------- fills
  fillRect(rect: Rectangle, color: number): void {
    this.$check();
    const [x, y, w, h] = rectToInts(rect);
    const [x0, y0, x1, y1] = regionClamped(x, y, w, h, this.$width, this.$height);
    if (x1 <= x0 || y1 <= y0) return;
    const c = premultiply(color >>> 0, this.$transparent);
    const W = this.$width;
    const data = this.$data;
    if (x0 === 0 && x1 === W) data.fill(c, y0 * W, y1 * W);
    else for (let yy = y0; yy < y1; yy++) data.fill(c, yy * W + x0, yy * W + x1);
    this.$touch(x0, y0, x1, y1);
  }

  /** 4-connected flood fill of the exact (premultiplied) colour at (x, y). */
  floodFill(x: number, y: number, color: number): void {
    this.$check();
    const W = this.$width;
    const H = this.$height;
    const xi = (x | 0) >>> 0;
    const yi = (y | 0) >>> 0;
    if (xi >= W || yi >= H) return;
    const data = this.$data;
    const expected = data[yi * W + xi];
    const replace = premultiply(color >>> 0, this.$transparent);
    if (expected === replace) return;
    // Scanline fill: produces exactly the same connected region as Flash's pixel-stack fill.
    let stack = new Int32Array(1024);
    let sp = 0;
    const push = (px: number, py: number) => {
      if (sp + 2 > stack.length) {
        const n = new Int32Array(stack.length * 2);
        n.set(stack);
        stack = n;
      }
      stack[sp++] = px;
      stack[sp++] = py;
    };
    push(xi, yi);
    let minX = xi;
    let minY = yi;
    let maxX = xi;
    let maxY = yi;
    while (sp > 0) {
      const py = stack[--sp];
      const px = stack[--sp];
      let row = py * W;
      if (data[row + px] !== expected) continue;
      let l = px;
      while (l > 0 && data[row + l - 1] === expected) l--;
      let r = px;
      while (r < W - 1 && data[row + r + 1] === expected) r++;
      data.fill(replace, row + l, row + r + 1);
      if (l < minX) minX = l;
      if (r > maxX) maxX = r;
      if (py < minY) minY = py;
      if (py > maxY) maxY = py;
      for (const ny of [py - 1, py + 1]) {
        if (ny < 0 || ny >= H) continue;
        row = ny * W;
        let inRun = false;
        for (let i = l; i <= r; i++) {
          if (data[row + i] === expected) {
            if (!inRun) {
              push(i, ny);
              inRun = true;
            }
          } else inRun = false;
        }
      }
    }
    this.$touch(minX, minY, maxX + 1, maxY + 1);
  }

  // ---------------------------------------------------------------- copyPixels
  copyPixels(
    source: BitmapData,
    sourceRect: Rectangle,
    destPoint: Point,
    alphaBitmapData: BitmapData | null = null,
    alphaPoint: Point | null = null,
    mergeAlpha: boolean = false,
  ): void {
    this.$check();
    if (!source) throw new TypeError('Error #2007: Parameter sourceBitmapData must be non-null.');
    source.$check();
    const [sx, sy, sw, sh] = rectToInts(sourceRect);
    const dx = +destPoint.x | 0;
    const dy = +destPoint.y | 0;
    mergeAlpha = !!mergeAlpha;
    if (alphaBitmapData) {
      alphaBitmapData.$check();
      const ax = alphaPoint ? +alphaPoint.x | 0 : 0;
      const ay = alphaPoint ? +alphaPoint.y | 0 : 0;
      if (alphaBitmapData === source && ax === sx && ay === sy) {
        this.$copyPixelsPlain(source, sx, sy, sw, sh, dx, dy, mergeAlpha);
      } else {
        this.$copyPixelsWithAlpha(source, sx, sy, sw, sh, dx, dy, alphaBitmapData, ax, ay, mergeAlpha);
      }
      return;
    }
    this.$copyPixelsPlain(source, sx, sy, sw, sh, dx, dy, mergeAlpha);
  }

  /** @internal Ruffle operations::copy_pixels + copy_on_cpu. */
  $copyPixelsPlain(source: BitmapData, sx: number, sy: number, sw: number, sh: number, dx: number, dy: number, mergeAlpha: boolean): void {
    const reg = clampWithIntersection(this.$width, this.$height, dx, dy, source.$width, source.$height, sx, sy, sw, sh);
    if (!reg) return;
    const [dX, dY, sX, sY, w, h] = reg;
    let blend = (source.$transparent && !this.$transparent) || mergeAlpha;
    if (!source.$transparent) blend = false;
    const W = this.$width;
    const SW = source.$width;
    const dst = this.$data;
    const src = source.$data;
    const opaque = !this.$transparent;
    if (source === this) {
      if (!blend && sX === dX && sY === dY) return;
      const reverse = dY - sY >= 0 && dX - sX >= 0;
      if (blend) {
        const px = (x: number, y: number) => {
          const di = (dY + y) * W + dX + x;
          dst[di] = blendOver(dst[di], dst[(sY + y) * W + sX + x]);
        };
        if (reverse) {
          for (let y = h - 1; y >= 0; y--) for (let x = w - 1; x >= 0; x--) px(x, y);
        } else {
          for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) px(x, y);
        }
      } else {
        const row = (y: number) => dst.copyWithin((dY + y) * W + dX, (sY + y) * W + sX, (sY + y) * W + sX + w);
        if (reverse) for (let y = h - 1; y >= 0; y--) row(y);
        else for (let y = 0; y < h; y++) row(y);
      }
    } else if (!blend && (this.$transparent || !source.$transparent)) {
      for (let y = 0; y < h; y++) {
        const so = (sY + y) * SW + sX;
        dst.set(src.subarray(so, so + w), (dY + y) * W + dX);
      }
    } else {
      for (let y = 0; y < h; y++) {
        let si = (sY + y) * SW + sX;
        let di = (dY + y) * W + dX;
        for (let x = 0; x < w; x++, si++, di++) {
          if (blend) {
            let c = blendOver(dst[di], src[si]);
            if (opaque) c = (c | 0xff000000) >>> 0;
            dst[di] = c;
          } else {
            dst[di] = (unmultiply(src[si]) | 0xff000000) >>> 0;
          }
        }
      }
    }
    this.$touch(dX, dY, dX + w, dY + h);
  }

  /** @internal Ruffle operations::copy_pixels_with_alpha_source (validated against Flash). */
  $copyPixelsWithAlpha(
    source: BitmapData,
    sx: number,
    sy: number,
    sw: number,
    sh: number,
    dx: number,
    dy: number,
    alpha: BitmapData,
    ax: number,
    ay: number,
    mergeAlpha: boolean,
  ): void {
    const W = this.$width;
    const H = this.$height;
    // Destination region check (encompassing pixels, clamped).
    const ex0 = Math.min(Math.max(dx, 0), Math.max(dx + sw, 0));
    const ex1 = Math.max(Math.max(dx, 0), Math.max(dx + sw, 0)) + 1;
    const ey0 = Math.min(Math.max(dy, 0), Math.max(dy + sh, 0));
    const ey1 = Math.max(Math.max(dy, 0), Math.max(dy + sh, 0)) + 1;
    if (Math.min(ex1, W) - Math.min(ex0, W) <= 0 || Math.min(ey1, H) - Math.min(ey0, H) <= 0) return;
    // Snapshots (Ruffle reads source/alpha areas before writing unless they are the target).
    const srcData = source === this ? null : source.$data;
    const alphaData = alpha === this ? null : alpha.$data;
    const dst = this.$data;
    const SW = source.$width;
    const SH = source.$height;
    const AW = alpha.$width;
    const AH = alpha.$height;
    const srcT = source.$transparent;
    const alphaT = alpha.$transparent;
    const dstT = this.$transparent;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (let srcY = sy; srcY < sy + sh; srcY++) {
      for (let srcX = sx; srcX < sx + sw; srcX++) {
        const destX = srcX - sx + dx;
        const destY = srcY - sy + dy;
        if (destX < 0 || destY < 0 || destX >= W || destY >= H) continue;
        let sc: number;
        if (srcData) {
          if (srcX < 0 || srcY < 0 || srcX >= SW || srcY >= SH) continue;
          sc = srcData[srcY * SW + srcX];
        } else {
          if (srcX < 0 || srcY < 0 || srcX >= W || srcY >= H) continue;
          sc = dst[srcY * W + srcX];
        }
        const di = destY * W + destX;
        let destColor = dst[di];
        const alphaX = srcX - sx + ax;
        const alphaY = srcY - sy + ay;
        const sA = sc >>> 24;
        let finalAlpha: number;
        if (alphaT) {
          let a: number;
          if (alphaData) {
            if (alphaX < 0 || alphaY < 0 || alphaX >= AW || alphaY >= AH) continue;
            a = alphaData[alphaY * AW + alphaX] >>> 24;
          } else {
            if (alphaX < 0 || alphaY < 0 || alphaX >= W || alphaY >= H) continue;
            a = dst[alphaY * W + alphaX] >>> 24;
          }
          finalAlpha = srcT ? (a === 255 ? sA : (a * sA) >> 8) : a;
        } else finalAlpha = srcT ? sA : 255;
        // Un-premultiply with float division + round (as Ruffle does), then re-premultiply.
        const af = sA / 255;
        const ur = u8cast(Math.round(((sc >>> 16) & 255) / af));
        const ug = u8cast(Math.round(((sc >>> 8) & 255) / af));
        const ub = u8cast(Math.round((sc & 255) / af));
        const inter = premultiply(((finalAlpha << 24) | (ur << 16) | (ug << 8) | ub) >>> 0, true);
        destColor = mergeAlpha || !dstT ? blendOver(destColor, inter) : inter;
        dst[di] = destColor;
        if (destX < minX) minX = destX;
        if (destY < minY) minY = destY;
        if (destX > maxX) maxX = destX;
        if (destY > maxY) maxY = destY;
      }
    }
    if (maxX >= minX) this.$touch(minX, minY, maxX + 1, maxY + 1);
  }

  // ---------------------------------------------------------------- other operations
  draw(source: any, matrix: Matrix | null = null, colorTransform: ColorTransform | null = null, blendMode: string | null = null, clipRect: Rectangle | null = null, smoothing: boolean = false): void {
    this.$check();
    if (!source) throw new TypeError('Error #2007: Parameter source must be non-null.');
    const touched = drawSourceInto(this, source, matrix, colorTransform, blendMode, clipRect, smoothing);
    if (touched) this.$touch(touched[0], touched[1], touched[2], touched[3]);
  }

  colorTransform(rect: Rectangle, ct: ColorTransform): void {
    this.$check();
    const rm = toFixed8(ct.redMultiplier);
    const gm = toFixed8(ct.greenMultiplier);
    const bm = toFixed8(ct.blueMultiplier);
    const am = toFixed8(ct.alphaMultiplier);
    const ro = toI16(ct.redOffset);
    const go = toI16(ct.greenOffset);
    const bo = toI16(ct.blueOffset);
    const ao = toI16(ct.alphaOffset);
    // Flash bug: only an alpha multiplier >= 1 with everything else identity has no effect.
    if (rm === 256 && gm === 256 && bm === 256 && am >= 256 && ro === 0 && go === 0 && bo === 0 && ao === 0) return;
    const [x, y, w, h] = rectToInts(rect);
    const x0 = Math.min(Math.max(x, 0), this.$width);
    const y0 = Math.min(Math.max(y, 0), this.$height);
    const x1 = Math.min(Math.max(x + w, 0), this.$width);
    const y1 = Math.min(Math.max(y + h, 0), this.$height);
    if (x1 <= x0 || y1 <= y0) return;
    const data = this.$data;
    const W = this.$width;
    const T = this.$transparent;
    for (let yy = y0; yy < y1; yy++) {
      for (let xx = x0; xx < x1; xx++) {
        const i = yy * W + xx;
        const c = unmultiply(data[i]);
        const a = c >>> 24;
        if (a === 0) continue;
        const nr = clamp255(((((c >>> 16) & 255) * rm) >> 8) + ro);
        const ng = clamp255(((((c >>> 8) & 255) * gm) >> 8) + go);
        const nb = clamp255((((c & 255) * bm) >> 8) + bo);
        const na = clamp255(((a * am) >> 8) + ao);
        data[i] = premultiply(((na << 24) | (nr << 16) | (ng << 8) | nb) >>> 0, T);
      }
    }
    this.$touch(x0, y0, x1, y1);
  }

  applyFilter(source: BitmapData, sourceRect: Rectangle, destPoint: Point, filter: any): void {
    this.$check();
    source.$check();
    if (!(filter instanceof BitmapFilter)) throw new TypeError('Error #2007: Parameter filter must be non-null.');
    const [sx, sy, sw, sh] = rectToInts(sourceRect);
    const dx = +destPoint.x | 0;
    const dy = +destPoint.y | 0;
    // Work on an unpremultiplied copy of the source area.
    const tmp = new BitmapData(Math.max(1, sw), Math.max(1, sh), true, 0);
    tmp.$copyPixelsPlain(source, sx, sy, sw, sh, 0, 0, false);
    if (filter instanceof ColorMatrixFilter) {
      const m = filter.$matrix;
      const d = tmp.$data;
      for (let i = 0; i < d.length; i++) {
        const c = unmultiply(d[i]);
        const r = (c >>> 16) & 255;
        const g = (c >>> 8) & 255;
        const b = c & 255;
        const a = c >>> 24;
        const nr = clamp255(Math.round(m[0] * r + m[1] * g + m[2] * b + m[3] * a + m[4]));
        const ng = clamp255(Math.round(m[5] * r + m[6] * g + m[7] * b + m[8] * a + m[9]));
        const nb = clamp255(Math.round(m[10] * r + m[11] * g + m[12] * b + m[13] * a + m[14]));
        const na = clamp255(Math.round(m[15] * r + m[16] * g + m[17] * b + m[18] * a + m[19]));
        d[i] = premultiply(((na << 24) | (nr << 16) | (ng << 8) | nb) >>> 0, true);
      }
    } else if (filter instanceof BlurFilter) {
      // UNVERIFIED: box blur approximation of Flash's blur (quality = passes).
      boxBlur(tmp, filter.blurX, filter.blurY, Math.max(1, filter.quality | 0));
    }
    // UNVERIFIED: other filters (glow, drop shadow, bevel, ...) are applied as identity here.
    this.$copyPixelsPlain(tmp, 0, 0, sw, sh, dx, dy, false);
  }

  generateFilterRect(sourceRect: Rectangle, _filter: any): Rectangle {
    return sourceRect.clone();
  }

  threshold(
    source: BitmapData,
    sourceRect: Rectangle,
    destPoint: Point,
    operation: string,
    threshold: number,
    color: number = 0,
    mask: number = 0xffffffff,
    copySource: boolean = false,
  ): number {
    this.$check();
    source.$check();
    const op = THRESHOLD_OPS[operation];
    if (!op) {
      const e = new Error('ArgumentError: Error #2005: Parameter 0 is of the incorrect type.');
      e.name = 'ArgumentError';
      throw e;
    }
    const [sx, sy, sw, sh] = rectToInts(sourceRect);
    const dx = +destPoint.x | 0;
    const dy = +destPoint.y | 0;
    const reg = clampWithIntersection(this.$width, this.$height, dx, dy, source.$width, source.$height, sx, sy, sw, sh);
    if (!reg) return 0;
    const [dX, dY, sX, sY, w, h] = reg;
    mask = mask >>> 0;
    const mt = (threshold >>> 0) & mask;
    const repl = premultiply(color >>> 0, true);
    const srcData = source === this ? null : source.$data.slice();
    const dst = this.$data;
    const W = this.$width;
    const SW = source.$width;
    let count = 0;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const sc = srcData ? srcData[(sY + y) * SW + sX + x] : dst[(sY + y) * W + sX + x];
        const di = (dY + y) * W + dX + x;
        if (op((sc & mask) >>> 0, mt)) {
          count++;
          dst[di] = repl;
        } else if (copySource) dst[di] = sc;
      }
    }
    this.$touch(dX, dY, dX + w, dY + h);
    return count;
  }

  getColorBoundsRect(mask: number, color: number, findColor: boolean = true): Rectangle {
    this.$check();
    mask = mask >>> 0;
    if (!this.$transparent) mask = (mask | 0xff000000) >>> 0;
    const c = premultiply(color >>> 0, this.$transparent);
    const W = this.$width;
    const H = this.$height;
    const data = this.$data;
    let minX = W;
    let maxX = 0;
    let minY = H;
    let maxY = 0;
    for (let y = 0; y < H; y++) {
      const row = y * W;
      for (let x = 0; x < W; x++) {
        const m = (data[row + x] & mask) >>> 0 === c;
        if (m === !!findColor) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX > 0 || maxY > 0) return new Rectangle(minX, minY, maxX - minX + 1, maxY - minY + 1);
    return new Rectangle(0, 0, 0, 0);
  }

  hitTest(firstPoint: Point, firstAlphaThreshold: number, secondObject: any, secondBitmapDataPoint?: Point, secondAlphaThreshold: number = 1): boolean {
    this.$check();
    const fx = +firstPoint.x | 0;
    const fy = +firstPoint.y | 0;
    const t1 = Math.min(255, Math.max(0, firstAlphaThreshold >>> 0));
    if (secondObject instanceof Point) {
      const x = (+secondObject.x | 0) - fx;
      const y = (+secondObject.y | 0) - fy;
      if (x < 0 || y < 0 || x >= this.$width || y >= this.$height) return false;
      return this.$data[y * this.$width + x] >>> 24 >= Math.max(1, t1);
    }
    if (secondObject instanceof Rectangle) {
      const [rx, ry, rw, rh] = rectToInts(secondObject);
      const [x0, y0, x1, y1] = regionClamped(rx - fx, ry - fy, rw, rh, this.$width, this.$height);
      for (let y = y0; y < y1; y++) for (let x = x0; x < x1; x++) if (this.$data[y * this.$width + x] >>> 24 >= t1) return true;
      return false;
    }
    let other: BitmapData | null = null;
    if (secondObject instanceof BitmapData) other = secondObject;
    else if (secondObject && secondObject.bitmapData instanceof BitmapData) other = secondObject.bitmapData;
    if (!other) throw new TypeError('Error #2005: Parameter 2 is of the incorrect type.');
    other.$check();
    const sp = secondBitmapDataPoint ?? new Point(0, 0);
    const t2 = Math.min(255, Math.max(0, secondAlphaThreshold >>> 0));
    const xd = (+sp.x | 0) - fx;
    const yd = (+sp.y | 0) - fy;
    const W = this.$width;
    const H = this.$height;
    const OW = other.$width;
    const OH = other.$height;
    let selfX0: number, testX0: number, width: number;
    if (xd < 0) {
      selfX0 = 0;
      testX0 = -xd;
      width = Math.max(0, Math.min(W, OW + xd));
    } else {
      selfX0 = xd;
      testX0 = 0;
      width = Math.max(0, Math.min(OW, W - xd));
    }
    let selfY0: number, testY0: number, height: number;
    if (yd < 0) {
      selfY0 = 0;
      testY0 = -yd;
      height = Math.max(0, Math.min(H, OH + yd));
    } else {
      selfY0 = yd;
      testY0 = 0;
      height = Math.max(0, Math.min(OH, H - yd));
    }
    for (let x = 0; x < width; x++) {
      for (let y = 0; y < height; y++) {
        const a = this.$data[(selfY0 + y) * W + selfX0 + x] >>> 24 >= t1;
        const b = other.$data[(testY0 + y) * OW + testX0 + x] >>> 24 >= t2;
        if (a && b) return true;
      }
    }
    return false;
  }

  scroll(x: number, y: number): void {
    this.$check();
    x = x | 0;
    y = y | 0;
    const W = this.$width;
    const H = this.$height;
    if ((x === 0 && y === 0) || Math.abs(x) >= W || Math.abs(y) >= H) return;
    const reverseY = y > 0;
    const reverseX = y === 0 && x > 0;
    const yFrom = reverseY ? H - y - 1 : -y;
    const yTo = reverseY ? -1 : H;
    const dy = reverseY ? -1 : 1;
    const xFrom = reverseX ? W - x - 1 : Math.max(-x, 0);
    const xTo = reverseX ? -1 : Math.min(W, W - x);
    const dx = reverseX ? -1 : 1;
    const data = this.$data;
    for (let sy = yFrom; sy !== yTo; sy += dy) {
      for (let sx = xFrom; sx !== xTo; sx += dx) {
        data[(sy + y) * W + sx + x] = data[sy * W + sx];
      }
    }
    this.$touch(0, 0, W, H);
  }

  copyChannel(source: BitmapData, sourceRect: Rectangle, destPoint: Point, sourceChannel: number, destChannel: number): void {
    this.$check();
    source.$check();
    const [sx, sy, sw, sh] = rectToInts(sourceRect);
    const dx = +destPoint.x | 0;
    const dy = +destPoint.y | 0;
    const reg = clampWithIntersection(this.$width, this.$height, dx, dy, source.$width, source.$height, sx, sy, sw, sh);
    if (!reg) return;
    const [dX, dY, sX, sY, w, h] = reg;
    const shiftOf = (ch: number) => (ch === 8 ? 24 : ch === 1 ? 16 : ch === 2 ? 8 : ch === 4 ? 0 : -1);
    const ss = shiftOf(sourceChannel >>> 0);
    const ds = shiftOf(destChannel >>> 0);
    if (ss < 0 || ds < 0) return;
    if (ds === 24 && !this.$transparent) return;
    const src = source.$data.slice();
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const sc = unmultiply(src[(sY + y) * source.$width + sX + x]);
        const v = (sc >>> ss) & 255;
        const di = (dY + y) * this.$width + dX + x;
        let dc = unmultiply(this.$data[di]);
        dc = ((dc & ~(255 << ds)) | (v << ds)) >>> 0;
        this.$data[di] = premultiply(dc, this.$transparent);
      }
    }
    this.$touch(dX, dY, dX + w, dY + h);
  }

  noise(randomSeed: number, low: number = 0, high: number = 255, channelOptions: number = 7, grayScale: boolean = false): void {
    this.$check();
    // Park-Miller "minimal standard" LCG as used by Flash's noise() (UNVERIFIED sequence parity).
    let seed = (randomSeed | 0) || 1;
    if (seed < 0) seed = -seed;
    const next = () => (seed = (seed * 16807) % 2147483647);
    low &= 255;
    high &= 255;
    const range = high - low + 1;
    const rnd = () => low + (next() % range);
    for (let i = 0; i < this.$data.length; i++) {
      let r = 0;
      let g = 0;
      let b = 0;
      let a = 255;
      if (grayScale) r = g = b = rnd();
      else {
        if (channelOptions & 1) r = rnd();
        if (channelOptions & 2) g = rnd();
        if (channelOptions & 4) b = rnd();
      }
      if (channelOptions & 8 && this.$transparent) a = rnd();
      this.$data[i] = premultiply(((a << 24) | (r << 16) | (g << 8) | b) >>> 0, this.$transparent);
    }
    this.$touch(0, 0, this.$width, this.$height);
  }

  getVector(rect: Rectangle): number[] {
    this.$check();
    const [x, y, w, h] = rectToInts(rect);
    const [x0, y0, x1, y1] = regionClamped(x, y, w, h, this.$width, this.$height);
    const out: number[] = [];
    for (let yy = y0; yy < y1; yy++) for (let xx = x0; xx < x1; xx++) out.push(this.getPixel32(xx, yy));
    return out;
  }

  setVector(rect: Rectangle, input: number[]): void {
    this.$check();
    const [x, y, w, h] = rectToInts(rect);
    const [x0, y0, x1, y1] = regionClamped(x, y, w, h, this.$width, this.$height);
    let i = 0;
    for (let yy = y0; yy < y1; yy++) {
      for (let xx = x0; xx < x1; xx++) {
        if (i >= input.length) throw new RangeError('Error #2006: The supplied index is out of bounds.');
        this.$data[yy * this.$width + xx] = premultiply(input[i++] >>> 0, this.$transparent);
      }
    }
    this.$touch(x0, y0, x1, y1);
  }

  clone(): BitmapData {
    this.$check();
    return BitmapData.$fromPremultiplied(this.$width, this.$height, this.$transparent, this.$data.slice());
  }

  dispose(): void {
    if (this.$disposed) return;
    this.$disposed = true;
    this.$data = new Uint32Array(0);
    this.$version++;
    const r = rt();
    r.renderVersion++;
    if (r.changedBitmaps !== null && !this.$dchanged) {
      this.$dchanged = true;
      r.changedBitmaps.push(this);
    }
  }

  lock(): void {
    this.$locked = true;
  }

  unlock(_changeRect?: Rectangle): void {
    this.$locked = false;
  }
}

function clamp255(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

function toI16(v: number): number {
  if (!Number.isFinite(v)) return 0;
  const t = Math.trunc(v);
  return t > 32767 ? 32767 : t < -32768 ? -32768 : t;
}

/** Rust `f64 as u8` (saturating, NaN -> 0). */
function u8cast(v: number): number {
  if (!(v === v)) return 0;
  return v <= 0 ? 0 : v >= 255 ? 255 : Math.trunc(v);
}

/** Separable box blur on premultiplied data (UNVERIFIED vs Flash's exact kernel). */
function boxBlur(bd: BitmapData, bx: number, by: number, passes: number): void {
  const W = bd.$width;
  const H = bd.$height;
  const rx = Math.max(0, Math.floor(bx / 2));
  const ry = Math.max(0, Math.floor(by / 2));
  const data = bd.$data;
  const tmp = new Uint32Array(data.length);
  const pass = (horizontal: boolean, r: number) => {
    if (r <= 0) return;
    const len = horizontal ? W : H;
    const lines = horizontal ? H : W;
    const n = 2 * r + 1;
    for (let l = 0; l < lines; l++) {
      for (let i = 0; i < len; i++) {
        let sa = 0;
        let sr = 0;
        let sg = 0;
        let sb = 0;
        for (let k = -r; k <= r; k++) {
          const j = i + k;
          if (j < 0 || j >= len) continue;
          const p = horizontal ? data[l * W + j] : data[j * W + l];
          sa += p >>> 24;
          sr += (p >>> 16) & 255;
          sg += (p >>> 8) & 255;
          sb += p & 255;
        }
        const idx = horizontal ? l * W + i : i * W + l;
        tmp[idx] = (((sa / n) << 24) | ((sr / n) << 16) | ((sg / n) << 8) | (sb / n)) >>> 0;
      }
    }
    data.set(tmp);
  };
  for (let p = 0; p < passes; p++) {
    pass(true, rx);
    pass(false, ry);
  }
}
